import NetInfo from '@react-native-community/netinfo';
import { isDetoxTestControlsEnabled } from '../config/environments';

type NetworkListener = (isOnline: boolean) => void;
export type DetoxMarkupCommand = 'add-arrow';
type MarkupCommandListener = (command: DetoxMarkupCommand) => void;
export type DetoxPhotoCommand = 'use-pending';
type PhotoCommandListener = (command: DetoxPhotoCommand) => void;
type ResultFocusListener = (reviewId: number | null) => void;
type ResultShareListener = (reviewId: number) => void;

let networkOverride: boolean | null = null;
let autoSyncPaused = false;
let focusedResultReviewId: number | null = null;
const networkListeners = new Set<NetworkListener>();
const markupCommandListeners = new Set<MarkupCommandListener>();
const photoCommandListeners = new Set<PhotoCommandListener>();
const resultFocusListeners = new Set<ResultFocusListener>();
const resultShareListeners = new Set<ResultShareListener>();

const toOnlineValue = (state: { isConnected?: boolean | null; isInternetReachable?: boolean | null }) =>
  Boolean(state.isConnected && (state.isInternetReachable ?? true));

export function getDetoxNetworkOverride(): boolean | null {
  if (!isDetoxTestControlsEnabled) {
    return null;
  }

  return networkOverride;
}

export function setDetoxNetworkOverride(isOnline: boolean | null) {
  if (!isDetoxTestControlsEnabled) {
    return;
  }

  networkOverride = isOnline;

  if (isOnline !== null) {
    networkListeners.forEach(listener => listener(isOnline));
  }
}

export function isDetoxAutoSyncPaused(): boolean {
  return isDetoxTestControlsEnabled && autoSyncPaused;
}

export function setDetoxAutoSyncPaused(isPaused: boolean) {
  if (!isDetoxTestControlsEnabled) {
    return;
  }

  autoSyncPaused = isPaused;
}

export function subscribeToDetoxNetworkOverride(listener: NetworkListener) {
  if (!isDetoxTestControlsEnabled) {
    return () => {};
  }

  networkListeners.add(listener);

  if (networkOverride !== null) {
    listener(networkOverride);
  }

  return () => {
    networkListeners.delete(listener);
  };
}

export function applyDetoxNetworkOverride(
  state: { isConnected?: boolean | null; isInternetReachable?: boolean | null }
) {
  const override = getDetoxNetworkOverride();
  return override ?? toOnlineValue(state);
}

export async function getEffectiveNetworkOnline() {
  const override = getDetoxNetworkOverride();
  if (override !== null) {
    return override;
  }

  const state = await NetInfo.fetch();
  return toOnlineValue(state);
}

export function emitDetoxMarkupCommand(command: DetoxMarkupCommand) {
  if (!isDetoxTestControlsEnabled) {
    return;
  }

  markupCommandListeners.forEach(listener => listener(command));
}

export function subscribeToDetoxMarkupCommands(listener: MarkupCommandListener) {
  if (!isDetoxTestControlsEnabled) {
    return () => {};
  }

  markupCommandListeners.add(listener);

  return () => {
    markupCommandListeners.delete(listener);
  };
}

export function emitDetoxPhotoCommand(command: DetoxPhotoCommand) {
  if (!isDetoxTestControlsEnabled) {
    return;
  }

  photoCommandListeners.forEach(listener => listener(command));
}

export function subscribeToDetoxPhotoCommands(listener: PhotoCommandListener) {
  if (!isDetoxTestControlsEnabled) {
    return () => {};
  }

  photoCommandListeners.add(listener);

  return () => {
    photoCommandListeners.delete(listener);
  };
}

export function emitDetoxResultFocusCommand(reviewId: number | null) {
  if (!isDetoxTestControlsEnabled) {
    return;
  }

  focusedResultReviewId = reviewId;
  resultFocusListeners.forEach(listener => listener(reviewId));
}

export function subscribeToDetoxResultFocus(listener: ResultFocusListener) {
  if (!isDetoxTestControlsEnabled) {
    return () => {};
  }

  resultFocusListeners.add(listener);
  listener(focusedResultReviewId);

  return () => {
    resultFocusListeners.delete(listener);
  };
}

export function emitDetoxResultShareCommand(reviewId: number) {
  if (!isDetoxTestControlsEnabled) {
    return;
  }

  resultShareListeners.forEach(listener => listener(reviewId));
}

export function subscribeToDetoxResultShare(listener: ResultShareListener) {
  if (!isDetoxTestControlsEnabled) {
    return () => {};
  }

  resultShareListeners.add(listener);

  return () => {
    resultShareListeners.delete(listener);
  };
}
